import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import ContactFormModal from '../components/ContactFormModal';
import { Trans } from 'react-i18next';
import Reveal from '../components/Reveal';
import i18n from '../i18n';
import heroVideo from '../assets/Hero-Video3.mp4';
import pic5 from '../assets/FusionX2.png';
import ideaImage from '../assets/38-idea.png';
import chartImage from '../assets/65-chart.png';
import dashboardImage from '../assets/Dashboard.png';
import menevaVideo from '../assets/meneva.mp4';
import menevaPoster from '../assets/meneva.jpg';
import dopamineBoostVideo from '../assets/Dopamin-boost.mp4';
import dopamineBoostPoster from '../assets/Dopamin-boost.png';
import highscoresVideo from '../assets/highscores.mp4';
import highscoresPoster from '../assets/highscores.jpg';
import winnersVideo from '../assets/winners.mp4';
import winnersPoster from '../assets/winners2.jpg';

const LINKS = {
  codespace: 'https://codespace.fi',
  privacy: 'https://codespace.fi/fusionxgame/saas/public/privacy-policy.php',
  terms: 'https://codespace.fi/fusionxgame/saas/public/terms.php',
  dashboard: 'https://play2visit.com/dashboard-demo',
};

const LinkInline = ({ href, children, className = '' }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`text-purple-400 underline hover:text-purple-300 ${className}`}
  >
    {children}
  </a>
);

const steps = [
  {
    label: '1️⃣',
    title: 'From post to play',
    copy: 'Traffic starts from creator content, paid social, email, or QR. Players land in your app or online store and start playing, no friction.',
    video: menevaVideo,
    poster: menevaPoster,
  },
  {
    label: '2️⃣',
    title: 'Tournaments',
    copy: 'Players compete in weekly tournaments. Scores, streaks, and boosters keep the experience engaging and brand-integrated.',
    video: dopamineBoostVideo,
    poster: dopamineBoostPoster,
  },
  {
    label: '3️⃣',
    title: 'In-store scoreboards & rewards',
    copy: 'When players visit selected locations in the real world, the game unlocks a scoreboard view revealing rankings and rewards.',
    video: highscoresVideo,
    poster: highscoresPoster,
  },
  {
    label: '4️⃣',
    title: 'Real-time engagement intelligence',
    copy: 'Every play, visit, and milestone is tracked by UUID. Your dashboard shows who played, who visited, and who needs a nudge — the moment it happens.',
    video: null,
    poster: null,
    bullets: [
      'Who played today',
      'Who visited your location',
      'Who\'s on a streak',
      'Who\'s cooling off',
      'Who churned and came back',
    ],
  },
];

const gameMoments = [
  { event: 'Player checks leaderboard on-site', action: '"Hey Mike, you\'re 5th — grab 25% off at Coffee Shop and push for the podium!"' },
  { event: 'Player enters top 10%', action: '"Hey Sarah, you\'re in the top 10%! The leaderboard is live at the store — come see where you stand."' },
  { event: 'Someone takes #1', action: '"Someone just dethroned the leader. The new #1 is live on the leaderboard at the store."' },
  { event: 'Player beats personal best', action: '"New personal best! You improved by 13%."' },
  { event: 'Player drops out of top 10%', action: '"Tom just knocked you out. Play again!"' },
];

const retentionEvents = [
  { event: 'Player\'s first ever game', action: '"Welcome to the game! Your first score is in."' },
  { event: 'Frequency drop — activity fell 50%+', action: '"You\'re slowing down! The competition is heating up — don\'t fall behind."' },
  { event: 'No score in 7 days', action: '"We haven\'t seen you in a while! A new tournament is running."' },
  { event: 'No score in 14 days', action: '"Come back and play — the leaderboard has changed!"' },
  { event: 'Player returns after 14+ days', action: '"Welcome back! You\'re ranked #67. Can you climb higher?"' },
];

const pricingPlans = [
  {
    name: 'Monthly',
    price: '€199',
    period: '/mo',
    billed: '€199',
    savings: null,
    highlighted: false,
  },
  {
    name: '6 Months',
    price: '€159',
    period: '/mo',
    billed: '€954 upfront',
    savings: 'Save 20%',
    highlighted: true,
  },
  {
    name: 'Annual',
    price: '€129',
    period: '/mo',
    billed: '€1,548 upfront',
    savings: 'Save 35%',
    highlighted: false,
  },
];

const locationTiers = [
  { tier: '1st location', price: 'Included in base' },
  { tier: '2–5 locations', price: '€89 / location / mo' },
  { tier: '6–15 locations', price: '€69 / location / mo' },
  { tier: '16+ locations', price: 'Custom / Enterprise' },
];

const Play2VisitV2 = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHeroReady, setIsHeroReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [caseStudyEmail, setCaseStudyEmail] = useState('');
  const [caseStudySent, setCaseStudySent] = useState(false);
  const { lng } = useParams();

  useEffect(() => {
    if (lng && i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [lng]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const handleHeroTimeUpdate = (event) => {
    const video = event.currentTarget;
    if (!isHeroReady && video.duration && video.currentTime >= Math.max(video.duration - 2, 0)) {
      setIsHeroReady(true);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleCaseStudySubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch('https://codespace.fi/fusionx/play2visitform.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Case Study Subscriber',
          email: caseStudyEmail,
          message: '[Case Study Signup] Wants to receive Menevä launch data.',
        }),
      });
      setCaseStudySent(true);
      setCaseStudyEmail('');
    } catch {
      // silently fail — the email is not critical
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white font-inter min-h-screen overflow-x-hidden">
      {/* ───── Section 1: HERO ───── */}
      <section
        className={`relative flex overflow-hidden ${
          isMobile ? 'flex-col items-center justify-start h-auto min-h-[90svh]' : 'items-center justify-center h-[90svh]'
        } md:h-screen`}
      >
        <video
          className={`transition-all duration-700 ${
            isMobile ? 'relative w-full h-auto max-h-[70vh] object-contain bg-black' : 'absolute inset-0 h-full w-full object-cover'
          }`}
          src={heroVideo}
          autoPlay
          playsInline
          muted
          onTimeUpdate={handleHeroTimeUpdate}
          onEnded={(event) => {
            event.currentTarget.pause();
            setIsHeroReady(true);
          }}
        />
        <div
          className={`relative z-10 container mx-auto px-4 md:px-8 flex ${
            isMobile ? 'justify-center mt-6' : 'justify-center md:justify-start'
          }`}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className={`bg-gray-950/80 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-2xl max-w-3xl space-y-6 ${
              isMobile ? 'w-full' : ''
            }`}
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.2 }}
              className="text-4xl md:text-6xl font-bold leading-tight space-y-2"
            >
              <span className="block text-purple-300">
                Stop renting attention, start owning engagement.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.35 }}
              className="text-lg md:text-xl text-gray-200"
            >
              See which customers visit, who's coming back, and who's about to churn — powered by a branded game they actually want to play.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.45 }}
              className="text-sm md:text-base text-gray-400"
            >
              For retail, malls, and apps that need repeat visits, not just impressions.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.5 }}
              className="pt-2 flex flex-wrap gap-4"
            >
              <button
                onClick={openModal}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Book a demo
              </button>
              <a
                href={LINKS.dashboard}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/30 hover:border-white/60 px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:bg-white/5"
              >
                See the dashboard
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ───── Section 2: ABOUT (compressed) ───── */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8 grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal direction="left">
            <img
              src={ideaImage}
              alt="Innovation concept illustration"
              className="w-3/5 mx-auto rounded-2xl shadow-xl object-cover"
            />
          </Reveal>
          <Reveal direction="right" delay={0.1}>
            <div className="space-y-5">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                About <span className="text-purple-300">Play2Visit</span>
              </h2>
              <p className="text-lg text-gray-200">
                Play2Visit embeds branded game tournaments directly into your app, website, or online store. You pass your customer's UUID in the game URL — we track every play, every visit, every milestone.
              </p>
              <p className="text-lg text-gray-200">
                Your customers compete in weekly tournaments promoted through your own channels. Your dashboard shows who's engaged, who's cooling off, and who just walked through your door.
              </p>
              <p className="text-lg text-gray-200">
                No SDK. No heavy integration. One API key, a webhook URL, and you're live.
              </p>
              <p className="text-lg md:text-xl font-semibold text-purple-200">
                From screentime to real-world visits.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── Section 3: HOW IT WORKS — 4 STEPS ───── */}
      <section className="py-20 md:py-24 bg-gray-900/60">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center">How it works</h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-2">
            {steps.map((step, index) => (
              <Reveal key={step.title} direction="up" delay={index * 0.1}>
                <div className="mx-auto w-full max-w-[22rem]">
                  <div className="relative aspect-[9/19] rounded-[3rem] border border-purple-500/40 bg-gradient-to-b from-gray-900/80 to-black/80 p-3 shadow-[0_30px_50px_-35px_rgba(139,92,246,0.8)]">
                    <div className="flex h-full flex-col rounded-[2.5rem] border border-white/5 bg-gray-950/90 px-6 py-6">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/25 text-xl">
                          {step.label}
                        </div>
                        <div>
                          <p className="text-[0.78rem] uppercase tracking-[0.4em] text-purple-300 font-semibold">Step {index + 1}</p>
                          <h3 className="text-lg font-semibold text-white leading-tight">{step.title}</h3>
                        </div>
                      </div>
                      {step.video ? (
                        <div className="mt-4 flex flex-1 items-center justify-center rounded-3xl border border-white/5 bg-black/40 overflow-hidden">
                          <video
                            src={step.video}
                            poster={step.poster}
                            controls
                            playsInline
                            preload="metadata"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : step.bullets ? (
                        <div className="mt-4 flex flex-1 flex-col justify-center rounded-3xl border border-white/5 bg-black/40 p-5 space-y-3">
                          {step.bullets.map((bullet) => (
                            <div key={bullet} className="flex items-center gap-3">
                              <span className="h-2 w-2 rounded-full bg-purple-400 shrink-0" />
                              <span className="text-sm text-gray-200">{bullet}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      <p className="mt-4 text-sm text-gray-300 leading-relaxed">{step.copy}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal direction="up" delay={0.2}>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-gray-900/60 p-6 text-gray-200">
                <span className="font-semibold text-white">Easy integration</span> · Pass your customer's UUID in the game URL. Set up a webhook to receive events. Your developer can make their first API call in under five minutes.
              </div>
              <div className="rounded-xl border border-white/10 bg-gray-900/60 p-6 text-gray-200">
                <span className="font-semibold text-white">Data & measurement</span> · Sessions, repeat play, store visits, app traffic, and tournament analytics — <span className="font-semibold text-purple-200">the metrics that matter to CMOs and growth teams.</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── Section 5: DASHBOARD ───── */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                See what's happening — the moment it happens
              </h2>
              <p className="text-lg text-gray-200">
                Your CRM knows who they are. Play2Visit tells you what they're doing.
              </p>
            </div>
          </Reveal>

          {/* Dashboard demo link card */}
          <Reveal direction="up" delay={0.1}>
            <a
              href={LINKS.dashboard}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-2xl mx-auto rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/30 to-gray-900/60 p-8 text-center hover:border-purple-400/50 transition-all duration-300 group"
            >
              <img src={dashboardImage} alt="Play2Visit dashboard screenshot" className="w-full mx-auto mb-4 rounded-xl shadow-lg" />
              <h3 className="text-xl font-bold text-white group-hover:text-purple-200 transition-colors">
                Explore the live dashboard demo
              </h3>
              <p className="text-gray-300 mt-2">
                Play the game, then watch your score appear live in the dashboard. See real-time player data, leaderboards, and notification events — all in one place.
              </p>
              <span className="inline-block mt-4 text-purple-400 font-semibold group-hover:text-purple-300">
                Open dashboard demo →
              </span>
            </a>
          </Reveal>

          {/* Notification events grid */}
          <div className="grid gap-8 md:grid-cols-2">
            <Reveal direction="left" delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 md:p-8 space-y-4">
                <h3 className="text-xl font-bold text-white">Competition Triggers — Turn Milestones Into Visits</h3>
                <p className="text-gray-300 text-sm">
                  Fire notifications when players hit milestones. Your app sends the message, your way, in your brand voice.
                </p>
                <ul className="space-y-3">
                  {gameMoments.map((item) => (
                    <li key={item.event} className="flex flex-col gap-1 border-b border-white/5 pb-3 last:border-0 last:pb-0">
                      <span className="text-sm font-semibold text-purple-300">{item.event}</span>
                      <span className="text-sm text-gray-400 italic">{item.action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal direction="right" delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 md:p-8 space-y-4">
                <h3 className="text-xl font-bold text-white">Retention Intelligence — Spot Risk Early</h3>
                <p className="text-gray-300 text-sm">
                  Play2Visit watches every player's behaviour and tells you when engagement changes. Act before they churn.
                </p>
                <ul className="space-y-3">
                  {retentionEvents.map((item) => (
                    <li key={item.event} className="flex flex-col gap-1 border-b border-white/5 pb-3 last:border-0 last:pb-0">
                      <span className="text-sm font-semibold text-purple-300">{item.event}</span>
                      <span className="text-sm text-gray-400 italic">{item.action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal direction="up" delay={0.15}>
            <div className="max-w-3xl mx-auto rounded-xl border border-white/10 bg-gray-900/60 p-5 text-sm text-gray-300 leading-relaxed text-center">
              <span className="font-semibold text-white">How to get the most from these notifications:</span> Design remote messages that hint at what happened without revealing exact rank — the full leaderboard only unlocks on the player's phone at your location. Once on-site, shift to targeted offers that drive spend. The less you reveal remotely, the more visits you drive.
            </div>
          </Reveal>

          <Reveal direction="up" delay={0.2}>
            <p className="text-center text-lg md:text-xl font-semibold text-purple-200 max-w-3xl mx-auto">
              This is not a game. It's a behavioural intelligence layer on top of your CRM.
            </p>
          </Reveal>

          <Reveal direction="up" delay={0.3}>
            <div className="max-w-3xl mx-auto mt-10 rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-900/20 to-gray-900/60 p-6 md:p-8 space-y-4">
              <h3 className="text-lg font-bold text-white">See it in action</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                A player enters the mall to check their leaderboard — visible only on-site, on their phone.
                Play2Visit detects the visit and fires a webhook to your CRM.
                Your CRM knows this customer and responds instantly:
              </p>
              <p className="text-purple-200 italic text-sm border-l-2 border-purple-500/40 pl-4">
                "Hey Mike, you're 5th — just two spots off the podium.
                Step into Coffee Shop and grab 25% off to fuel your next move."
              </p>
              <p className="text-gray-400 text-sm font-semibold">
                Geofenced content drives the visit. Real-time intelligence closes the sale.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── Section 6: CUSTOMER SCENARIOS ───── */}
      <section className="py-20 md:py-24 bg-gray-900/60">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-white">Real customer scenarios</h2>
              <p className="text-gray-300">All prices excl. VAT.</p>
            </div>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-2">
            <Reveal direction="left" delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 md:p-8 space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-white">Mall (physical location)</h3>
                </div>
                <div className="space-y-2 text-gray-200">
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-400">Base plan</span>
                    <span>€199 / mo</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-400">Est. verified visits</span>
                    <span>~800 / mo</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-400">Event billing</span>
                    <span>700 × €1 = €700</span>
                  </div>
                  <div className="flex justify-between pt-2 font-bold text-white">
                    <span>Estimated total</span>
                    <span className="text-purple-300">~€899 / mo</span>
                  </div>
                </div>
                <p className="text-sm text-gray-300">
                  800 named CRM customers verified at the mall each month. Real-time notifications on milestones and churn risk.
                </p>
                <div className="rounded-xl bg-white/5 p-4 text-sm text-gray-400">
                  <span className="font-semibold text-gray-300">Comparable:</span> Location-based foot traffic ads — €5,000–15,000 / mo
                </div>
              </div>
            </Reveal>
            <Reveal direction="right" delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 md:p-8 space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-white">Taxi company (app only)</h3>
                </div>
                <div className="space-y-2 text-gray-200">
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-400">Base plan</span>
                    <span>€199 / mo</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-400">Est. unique players</span>
                    <span>~1,200 / mo</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-400">Event billing</span>
                    <span>1,100 × €1 = €1,100</span>
                  </div>
                  <div className="flex justify-between pt-2 font-bold text-white">
                    <span>Estimated total</span>
                    <span className="text-purple-300">~€1,299 / mo</span>
                  </div>
                </div>
                <p className="text-sm text-gray-300">
                  1,200 identified app users engaged per month. Automatic churn detection and re-engagement triggers via webhook.
                </p>
                <div className="rounded-xl bg-white/5 p-4 text-sm text-gray-400">
                  <span className="font-semibold text-gray-300">Comparable:</span> Paid re-engagement ads — €3,200–€8,000 / mo
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal direction="up" delay={0.2}>
            <div className="text-center">
              <p className="text-lg font-semibold text-purple-200">
                Why €1 per event is a bargain
              </p>
              <p className="text-gray-300 mt-2 max-w-2xl mx-auto">
                The average mall visitor spends €30–50 per visit. A taxi ride averages €15–25. You're paying €1 for a verified, named customer you can reward and retain.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── PRICING ───── */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-white">Simple, transparent pricing</h2>
              <p className="text-gray-300">A platform fee and a price per result. That's it.</p>
            </div>
          </Reveal>

          {/* Pricing cards */}
          <div className="grid gap-6 md:grid-cols-3 max-w-4xl mx-auto">
            {pricingPlans.map((plan) => (
              <Reveal key={plan.name} direction="up" delay={0.1}>
                <div
                  className={`rounded-2xl border p-6 md:p-8 text-center space-y-4 ${
                    plan.highlighted
                      ? 'border-purple-400 bg-gradient-to-b from-purple-900/40 to-gray-900/60 shadow-[0_0_30px_-10px_rgba(139,92,246,0.5)]'
                      : 'border-white/10 bg-gray-900/60'
                  }`}
                >
                  {plan.savings && (
                    <span className="inline-block bg-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      {plan.savings}
                    </span>
                  )}
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <div>
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-400">{plan.period}</span>
                  </div>
                  <p className="text-sm text-gray-400">Billed {plan.billed}</p>
                  <ul className="text-xs text-gray-400 text-left space-y-1.5 pt-2 border-t border-white/5">
                    <li className="flex items-center gap-2"><span className="text-purple-400">✓</span> Tournament engine</li>
                    <li className="flex items-center gap-2"><span className="text-purple-400">✓</span> Analytics dashboard</li>
                    <li className="flex items-center gap-2"><span className="text-purple-400">✓</span> Real-time notifications</li>
                    <li className="flex items-center gap-2"><span className="text-purple-400">✓</span> Full API access</li>
                    <li className="flex items-center gap-2"><span className="text-purple-400">✓</span> 1 location included</li>
                    <li className="flex items-center gap-2"><span className="text-purple-400">✓</span> First 100 events free</li>
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Add-ons */}
          <Reveal direction="up" delay={0.2}>
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Events */}
              <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 md:p-8 space-y-4">
                <h3 className="text-lg font-bold text-white">Billable events — €1 per event</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">
                    <span className="font-semibold text-purple-300">Physical location</span> — each verified visit counts as one event. If Mike visits the mall 3 times this month, that's 3 events.
                  </p>
                  <p className="text-gray-300">
                    <span className="font-semibold text-purple-300">Virtual (app-only)</span> — each unique identified player per month counts as one event. If Mike plays 50 times in your taxi app this month, that's 1 event.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="rounded-xl bg-white/5 p-4">
                    <p className="font-semibold text-purple-300">First 100 events / location / mo</p>
                    <p className="text-gray-400">Free — always included</p>
                  </div>
                  <div className="rounded-xl bg-white/5 p-4">
                    <p className="font-semibold text-purple-300">From event 101 onwards</p>
                    <p className="text-gray-400">€1.00 / event</p>
                  </div>
                </div>
              </div>

              {/* Locations */}
              <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 md:p-8 space-y-4">
                <h3 className="text-lg font-bold text-white">Additional locations</h3>
                <div className="space-y-2">
                  {locationTiers.map((tier) => (
                    <div key={tier.tier} className="flex justify-between text-sm border-b border-white/5 pb-2 last:border-0 last:pb-0">
                      <span className="text-gray-300">{tier.tier}</span>
                      <span className="text-gray-200 font-medium">{tier.price}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Integration callout */}
              <div className="rounded-2xl border border-purple-500/20 bg-purple-900/10 p-6 text-center space-y-2">
                <p className="text-lg font-semibold text-white">
                  One API key. Five minutes to your first API call.
                </p>
                <p className="text-sm text-gray-400">
                  No SDK to install. No libraries to maintain. Just a URL and a key.
                </p>
              </div>

              {/* Objection handling */}
              <div className="rounded-2xl border border-white/10 bg-gray-900/60 p-6 text-center space-y-2">
                <p className="text-lg font-semibold text-white">
                  Every billable event is tracked on our infrastructure.
                </p>
                <p className="text-sm text-gray-400">
                  You don't need to report anything — we measure it. You can verify every event in your dashboard against your own CRM data.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal direction="up" delay={0.3}>
            <p className="text-center text-sm text-gray-500">
              16+ locations?{' '}
              <button onClick={openModal} className="text-purple-400 underline hover:text-purple-300">
                Contact sales
              </button>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <Reveal direction="up">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-purple-200">
                Ready to turn screentime into store time?
              </h2>
              <p className="text-lg text-gray-200">
                Book a 30-minute call and we'll map out how Play2Visit fits your channels, your app, and your customers.
              </p>
              <div className="pt-2">
                <button
                  onClick={openModal}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Book a demo
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── TESTIMONIAL ───── */}
      <section className="py-20 md:py-24 bg-gray-950/70 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8">
          <Reveal direction="up">
            <div className="max-w-3xl mx-auto space-y-6">
              <blockquote className="border-l-4 border-purple-500/40 pl-6 space-y-4 text-lg text-gray-200 leading-relaxed italic">
                <p>
                  "Play2Visit was first introduced to me as a marketer exploring new ways to activate our brand and app at{' '}
                  <a
                    href="https://meneva.fi/en"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-white not-italic underline decoration-purple-300/60 decoration-dotted underline-offset-4 hover:text-purple-200"
                  >
                    Menevä
                  </a>
                  , Finland's largest ride service. As I saw its potential, I realized it could solve far more than one brand's challenge.
                </p>
                <p>
                  That's why I decided to join as an advisor — to help shape the concept and bring a marketer's perspective into this innovation."
                </p>
              </blockquote>
              <div className="flex items-center gap-4 pl-6">
                <img src="/arto.png" alt="Arto Peisa" className="w-12 h-12 rounded-full border-2 border-purple-400/50 object-cover" />
                <div>
                  <p className="text-white font-semibold">Arto Peisa</p>
                  <p className="text-gray-400 text-sm">CMO of Menevä — Play2Visit Advisor</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── CASE STUDY SIGNUP ───── */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <Reveal direction="up">
            <div className="max-w-2xl mx-auto rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-900/20 to-gray-900/60 p-8 md:p-10 text-center space-y-5">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-purple-400">Coming this spring</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                First real-world results from a national brand
              </h2>
              <p className="text-gray-300 leading-relaxed">
                We're launching Play2Visit with Finland's largest ride service. Be the first to see verified engagement and visit data when it goes live.
              </p>
              {caseStudySent ? (
                <p className="text-green-400 font-semibold py-2">
                  You're on the list. We'll send the data as soon as it's live.
                </p>
              ) : (
                <form onSubmit={handleCaseStudySubmit} className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                  <input
                    type="email"
                    required
                    value={caseStudyEmail}
                    onChange={(e) => setCaseStudyEmail(e.target.value)}
                    placeholder="Your work email"
                    className="px-5 py-3 rounded-full bg-gray-800 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 w-full sm:w-72"
                  />
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
                  >
                    Get the case study first
                  </button>
                </form>
              )}
              <p className="text-xs text-gray-500">No spam. One email when the data is ready.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───── Footer ───── */}
      <footer className="py-16 md:py-20 border-t border-gray-700/50 text-center space-y-3">
        <p className="text-gray-500 text-sm">
          <Trans
            i18nKey="footer.line"
            values={{ year: new Date().getFullYear() }}
            components={{
              codespace: <LinkInline href={LINKS.codespace} />,
              privacy: <a href={LINKS.privacy} className="hover:text-white" />,
              terms: <a href={LINKS.terms} className="hover:text-white" />,
              sep: <span className="px-2" />,
            }}
          />
        </p>
        <p className="text-gray-600 text-xs">
          Codespace · VAT ID 1492928-3 · Digi & Game Center, Matarankatu 2, 40100 Jyväskylä, Finland
        </p>
      </footer>

      {isModalOpen && <ContactFormModal onClose={closeModal} />}
    </div>
  );
};

export default Play2VisitV2;
